import { format, parseISO } from "date-fns"
import { enUS, fr } from "date-fns/locale"
import { CalendarIcon } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

interface DatePopoverProps {
  // ISO date string ("YYYY-MM-DD") or empty/null when unset.
  value: string | null | undefined
  // Called with an ISO date string when the user picks a day.
  onChange: (iso: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
}

export function DatePopover({
  value,
  onChange,
  placeholder,
  className,
  disabled,
}: DatePopoverProps) {
  const { t, i18n } = useTranslation("common")
  const dateLocale = i18n.language?.startsWith("fr") ? fr : enUS
  const selected = isoToDate(value)

  return (
    <Popover>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "w-full justify-start font-normal",
            !selected && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="size-4" />
          {selected
            ? format(selected, "PPP", { locale: dateLocale })
            : (placeholder ?? t("date.pickPlaceholder", { defaultValue: "Pick a date" }))}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => {
            if (d) onChange(format(d, "yyyy-MM-dd"))
          }}
          locale={dateLocale}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
}

function isoToDate(value: string | null | undefined): Date | undefined {
  if (!value) return undefined
  // Accept either "YYYY-MM-DD" or full ISO; parseISO handles both.
  const d = parseISO(value)
  return Number.isNaN(d.getTime()) ? undefined : d
}
